// ** React Imports
import { useEffect, useState, useRef, useCallback } from "react";

// ** MUI Imports
import {
  CircularProgress,
  Backdrop,
} from "@mui/material";
import Box from "@mui/material/Box";
import useMediaQuery from "@mui/material/useMediaQuery";

// ** Redux Imports
import { useDispatch, useSelector } from "react-redux";

// ** Hooks
import { useSettings } from "components/hooks/useSettings";

// ** FullCalendar & App Components Imports
import Calendar from "./Calendar";
import Topbar from "./Topbar";
import AddEventSidebar from "./AddEventSidebar";
import CalendarWrapper from "components/styles/libs/fullcalendar";
import JobDetailDialog from "screens/WorkOrders/component/JobDetail";

// ** Logic Imports
import { useDispatchBoardPermission } from "screens/DispatchBoard/permissions/DispatchBoardPermissions";

// ** Actions
import {
  addEvent,
  fetchEvents,
  deleteEvent,
  updateEvent,
  handleSelectEvent,
  handleAllCalendars,
  handleCalendarsUpdate,
} from "store/apps/calendar";
import { getJobWorkRequirement } from "screens/DispatchBoard/logic/DispatchBoardLogic";
import { useNavigate } from "react-router-dom";
import { useWorkRequirementLogic } from "screens/WorkOrders/logic/work-requirement-logic";

// ** CalendarColors
const calendarsColor = {
  Job: "success",
  Assessment: "info",
};

const AppCalendar = ({
  toast,
  onJobCreated,
  calendarSelectedDate,
  setCalendarSelectedDate,
  calendarViewType,
  setCalendarViewType,
  appliedFilters,
  setAppliedFilters
}) => {
  //#region Permissions
  const { DispatchBoardPermissionProps } = useDispatchBoardPermission();
  const {
    hasDbGetPermission,
    hasDbSavePermission,
    hasDbDeletePermission
  } = DispatchBoardPermissionProps;
  //#endregion Permissions

  // ** States
  const [calendarApi, setCalendarApi] = useState(null);
  const [leftSidebarOpen, setLeftSidebarOpen] = useState(false);
  const [addEventSidebarOpen, setAddEventSidebarOpen] = useState(false);

  // Job
  const [jobDetailOpen, setJobDetailOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedWorkReq, setSelectedWorkReq] = useState(null);
  const [rules, setRules] = useState({ 
    canEdit: true,
    canDelete: true,
    canSubmit: true,
    nextStatuses: [],
    cannotGoBack: false
  });
  const [loadingJobData, setLoadingJobData] = useState(false);
  const [calendarRefreshKey, setCalendarRefreshKey] = useState(0);
  
  const navigate = useNavigate();

  const { WorkRequirementsProps } = useWorkRequirementLogic({
    requestID: null,           // we’ll override per‐WR below
    navigate,
    toast,
    setLoading: setLoadingJobData,
    serviceRequest: {}         // dummy, since we only need the fetch fn
  });

  const { fetchWorkRequirements } = WorkRequirementsProps;

  const refreshCalendarData = () => {
    setCalendarRefreshKey(prev => prev + 1);
    // if (hasDbGetPermission) {
      dispatch(fetchEvents(store.selectedCalendars));
    // }
  };
  
  const handleJobPopupOpen = async (eventData) => {

    const { workReqID, jobID } = eventData.extendedProps;

    if (!workReqID || !jobID) {
      toast.error("Missing work requirement or job data");
      return;
    }

    setLoadingJobData(true);

    try {
      // Fetch the actual work requirement data
      const workRequirements = await getJobWorkRequirement(workReqID, navigate);
      
      if (!workRequirements || workRequirements.length === 0) {
        console.error("Work requirement not found");
        return;
      }
  
      const workReq = workRequirements[0];
      
      // Find the specific job by jobID
      const job = workReq.jobs.find(j => j.jobID === jobID);
      
      if (!job) {
        console.error("Job not found in work requirement");
        return;
      }
  
      setSelectedWorkReq(workReq);
      setSelectedJob(job);
      setJobDetailOpen(true);
      
    } catch (error) {
      toast.error(`Error loading job details: ${error.message}`);
      console.error("Error fetching work requirement:", error);
    } finally {
      setLoadingJobData(false);
    }
    
  };
  
  const handleJobPopupClose = () => {
    setJobDetailOpen(false);
    setSelectedWorkReq(null);
    setSelectedJob(null);
    refreshCalendarData();
  };
  // Job

  // ** Hooks
  const { settings } = useSettings();
  const dispatch = useDispatch();
  const store = useSelector((state) => state.calendar);
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  // ** Vars
  const leftSidebarWidth = 300;
  const addEventSidebarWidth = 400;
  const { skin, direction } = settings;
  const mdAbove = useMediaQuery((theme) => theme.breakpoints.up("md"));

  // Memoized permission object
  const permissions = useCallback(() => ({
    hasDbGetPermission,
    hasDbSavePermission,
    hasDbDeletePermission
  }), [hasDbGetPermission, hasDbSavePermission, hasDbDeletePermission]);

  useEffect(() => {
    if(hasDbGetPermission){
      dispatch(fetchEvents(store.selectedCalendars));
    }
  }, [dispatch, store.selectedCalendars, hasDbGetPermission, permissions]);

  const handleLeftSidebarToggle = () => setLeftSidebarOpen(!leftSidebarOpen);
  const handleAddEventSidebarToggle = () =>
    setAddEventSidebarOpen(!addEventSidebarOpen);

  return (
    <>
      <Topbar
        store={store}
        mdAbove={mdAbove}
        dispatch={dispatch}
        calendarApi={calendarApi}
        calendarsColor={calendarsColor}
        leftSidebarOpen={leftSidebarOpen}
        leftSidebarWidth={leftSidebarWidth}
        handleSelectEvent={handleSelectEvent}
        handleAllCalendars={handleAllCalendars}
        handleCalendarsUpdate={handleCalendarsUpdate}
        handleLeftSidebarToggle={handleLeftSidebarToggle}
        handleAddEventSidebarToggle={handleAddEventSidebarToggle}
        calendarSelectedDate={calendarSelectedDate}
        setCalendarSelectedDate={setCalendarSelectedDate}
        appliedFilters={appliedFilters}
        setAppliedFilters={setAppliedFilters}
      />
      <CalendarWrapper
        className="app-calendar"
        sx={{
          boxShadow: skin === "bordered" ? 0 : 6,
          ...(skin === "bordered" && {
            border: (theme) => `1px solid ${theme.palette.divider}`,
          }),
          overflowX: isSmallScreen ? "auto" : "hidden",
          WebkitOverflowScrolling: "touch", // For smooth scrolling on iOS
        }}
      >
        <Box
          sx={{
            p: 6,
            pb: 0,
            flexGrow: 1,
            borderRadius: 1,
            boxShadow: "none",
            backgroundColor: "background.paper",
            minWidth: isSmallScreen ? "800px" : "auto", // Ensure calendar has enough width
            ...(mdAbove
              ? { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }
              : {}),
          }}
        >
          <Calendar
            store={store}
            dispatch={dispatch}
            direction={direction}
            updateEvent={updateEvent}
            calendarApi={calendarApi}
            calendarsColor={calendarsColor}
            setCalendarApi={setCalendarApi}
            handleSelectEvent={handleSelectEvent}
            handleLeftSidebarToggle={handleLeftSidebarToggle}
            handleAddEventSidebarToggle={handleAddEventSidebarToggle}
            handleJobPopupOpen={handleJobPopupOpen} // this is for job
            isSmallScreen={isSmallScreen}
            appliedFilters={appliedFilters}
            setCalendarViewType={setCalendarViewType}
            
          />
        </Box>
        <AddEventSidebar
          store={store}
          dispatch={dispatch}
          addEvent={addEvent}
          updateEvent={updateEvent}
          deleteEvent={deleteEvent}
          calendarApi={calendarApi}
          drawerWidth={addEventSidebarWidth}
          handleSelectEvent={handleSelectEvent}
          addEventSidebarOpen={addEventSidebarOpen}
          handleAddEventSidebarToggle={handleAddEventSidebarToggle}
          toast={toast}
          onJobCreated={onJobCreated}
        />

        {/* Jobs */}
        <JobDetailDialog
          open={jobDetailOpen}
          handleClose={handleJobPopupClose}
          method="detail"
          entity="workOrder-detail"
          rules={rules}
          workReq={selectedWorkReq}
          job={selectedJob}
          setJob={setSelectedJob}
          fetchWorkRequirements={fetchWorkRequirements} // You might need to pass this
          setLoading={() => {}} // You might need to pass this
          toast={toast}
          fetchRequests={() => {}}          
        />

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loadingJobData}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      </CalendarWrapper>
    </>
  );
};

export default AppCalendar;